import { useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast } from "sonner";
import { uploadImage } from "@/lib/api";

// Branded rich text editor used for email templates. Images are uploaded to
// object storage (absolute URL) so they render in external email clients.
export const RichEditor = forwardRef(function RichEditor({ value, onChange }, ref) {
  const quillRef = useRef(null);

  useImperativeHandle(ref, () => ({
    insertText(text) {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
      editor.insertText(range.index, text, "user");
      editor.setSelection(range.index + text.length, 0);
    },
  }));

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await uploadImage(file);
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true) || { index: editor.getLength() };
        editor.insertEmbed(range.index, "image", url, "user");
        editor.setSelection(range.index + 1, 0);
      } catch {
        toast.error("Image upload failed");
      }
    };
    input.click();
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline"],
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: { image: imageHandler },
      },
    }),
    [imageHandler]
  );

  return (
    <div className="suym-rich-editor" data-testid="rich-editor">
      <ReactQuill ref={quillRef} theme="snow" value={value || ""} onChange={onChange} modules={modules} />
    </div>
  );
});
